import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Check, Plus, Search, X } from 'lucide-react';
import axiosInstance from '../../../../../api/axiosInstance';
import { BOARD_CODES_FULL, BOARD_CODE_TO_NAME } from '../../../../../components/Booking/bookingConstants';
import {
  VACATION_COUNT_SCOPE,
  VACATION_FILTER_PRESETS,
  VACATION_LOW_MATCH,
} from '../HomepageSettings.constants';
import styles from '../HomepageSettings.module.css';

const FACETS_URL = '/hotel-filters/facets';

// De-duplication identity for one criterion. `filterKey` has to be part of it: code 340 is
// both a group-70 facility and a kids amenity, and those are two different filters.
const critKey = (c) => `${c.filterKey}:${c.value}`;

const asArray = (v) => (Array.isArray(v) ? v : []);

/**
 * The scope's whole facet catalogue, fetched once for the page and shared by every card.
 * `/hotel-filters/facets` answers with the catalogue AND the filtered hotel count in one
 * response; the catalogue half depends only on the scope, so re-requesting it on every
 * keystroke would ship tens of kilobytes to say the same thing.
 */
let cataloguePromise = null;

function loadCatalogue() {
  if (!cataloguePromise) {
    cataloguePromise = axiosInstance
      .get(FACETS_URL, {
        params: { destinations: VACATION_COUNT_SCOPE.join(','), codes: false, attrs: false },
      })
      .then((res) => buildCatalogue(res?.data?.data?.facets ?? {}))
      .catch((err) => {
        cataloguePromise = null; // let the next card that opens the picker retry
        throw err;
      });
  }
  return cataloguePromise;
}

/** Flatten the facet response into one searchable list of pickable criteria. */
function buildCatalogue(facets) {
  const out = [];
  const seen = new Set();
  const push = (filterKey, kind, value, label, hotels) => {
    if (value == null || value === '' || !label) return;
    const entry = { filterKey, kind, value, label: String(label), hotels };
    const key = critKey(entry);
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ ...entry, key });
  };

  for (const r of asArray(facets.stars)) push('stars', 'Stars', Number(r.stars), `${r.stars}-star`, r.hotels);
  for (const r of asArray(facets.holiday)) push('themes', 'Holiday type', Number(r.id), r.name, r.hotels);
  for (const r of asArray(facets.facilities)) push('facilities', 'Facility', Number(r.code), r.name, r.hotels);
  // An activity is stored group-qualified whenever the facet row says which group it came
  // from, because codes are reused across groups — bare 620 matches Spa centre AND Waterpark.
  // A row without `groupCode` came from an older backend and can only be stored bare, which
  // keeps the legacy multi-group match rather than guessing a group.
  for (const r of asArray(facets.activities)) {
    const value = r.groupCode != null ? `${r.groupCode}:${r.code}` : Number(r.code);
    push('activities', 'Activity', value, r.name, r.hotels);
  }
  for (const r of asArray(facets.kids)) push('kids', 'For kids', Number(r.code), r.name, r.hotels);
  for (const r of asArray(facets.accommodation)) push('accommodation', 'Property type', Number(r.code), r.name, r.hotels);
  // Board is not a facet: it lives on the priced rate, not on the hotel record, so it can
  // neither come from this response nor be counted below. The codes are the same shared list
  // the booking forms use, so board naming stays consistent across the dashboard.
  for (const b of BOARD_CODES_FULL) push('boards', 'Board', b.code, b.name, null);

  return out;
}

/** Query params for the match count: the default scope narrowed by the picked criteria. */
function countParams(criteria) {
  const params = { destinations: VACATION_COUNT_SCOPE.join(','), codes: false, attrs: false };
  for (const c of criteria) {
    if (c.filterKey === 'adultsOnly') {
      params.adultsOnly = 1;
      continue;
    }
    params[c.filterKey] = params[c.filterKey] == null
      ? String(c.value)
      : `${params[c.filterKey]},${c.value}`;
  }
  return params;
}

/**
 * Builds the hotel search one "Favorite type of vacation" card links to.
 *
 * The client is a travel agent, not an engineer: a facility code must never reach the
 * screen. Presets carry the verified codes, the picker carries whatever the live catalogue
 * offers, and both are labelled with the name the website will print on the card.
 *
 * `onPatch` receives a partial `search` object; the caller merges it and keeps `boardCode`
 * in sync, so criteria and the legacy mirror are always written together.
 */
export default function VacationSearchEditor({ criteria, showFacts, boardCode, active, onPatch }) {
  // Same precedence the website applies: a card saved before criteria existed carries only
  // `boardCode` and still links to a board search, so it is shown as that one chip. This is a
  // read, not a migration — nothing is written until the client edits, so an untouched legacy
  // card saves back byte-identical.
  const picked = useMemo(() => {
    if (Array.isArray(criteria) && criteria.length) return criteria;
    if (boardCode) {
      return [{ filterKey: 'boards', value: boardCode, label: BOARD_CODE_TO_NAME[boardCode] || boardCode }];
    }
    return [];
  }, [criteria, boardCode]);
  const pickedKeys = useMemo(() => new Set(picked.map(critKey)), [picked]);

  const apply = (next) => onPatch({ criteria: next });
  const addCriterion = (c) =>
    apply([...picked, { filterKey: c.filterKey, value: c.value, label: c.label }]);
  const removeCriterion = (key) => apply(picked.filter((c) => critKey(c) !== key));
  const togglePreset = (p) =>
    (pickedKeys.has(critKey(p)) ? removeCriterion(critKey(p)) : addCriterion(p));

  // ── Live match count ──────────────────────────────────────────────────────
  // Board is filtered out: the facets endpoint only knows hotel content, so passing a board
  // code would be ignored and the number would silently overstate the match.
  const countable = picked.filter((c) => c.filterKey !== 'boards');
  const countKey = countable.map(critKey).sort().join('|');
  const boardsOnly = picked.length > 0 && countable.length === 0;

  const [count, setCount] = useState({ status: 'idle' });
  const reqIdRef = useRef(0);

  useEffect(() => {
    // `active` is the accordion state, not visibility guesswork: a collapsed section is only
    // hidden with CSS, so every card stays mounted and would otherwise fire its count on page
    // load for a panel nobody opened.
    if (!active || !countKey) {
      setCount({ status: 'idle' });
      return undefined;
    }
    let cancelled = false;
    const myId = ++reqIdRef.current;
    setCount({ status: 'loading' });
    const t = setTimeout(() => {
      axiosInstance
        .get(FACETS_URL, { params: countParams(countable) })
        .then((res) => {
          if (cancelled || myId !== reqIdRef.current) return;
          // Deliberately local state, not a patch back into the config: this resolves up to a
          // second after the click, and `onPatch` writes a whole `vacationTypes` array rebuilt
          // from the config it closed over — anything typed into the card meanwhile would be
          // clobbered. The count is re-measured every time the card is opened anyway.
          setCount({ status: 'ok', hotels: Number(res?.data?.data?.scope?.hotelCount ?? 0) });
        })
        .catch(() => {
          if (!cancelled && myId === reqIdRef.current) setCount({ status: 'error' });
        });
    }, 500);
    return () => { cancelled = true; clearTimeout(t); };
    // Keyed on the serialised criteria alone. `countable` is rebuilt on every render of the
    // parent form, so listing it would restart the debounce (and refire the count) on every
    // keystroke anywhere else in the homepage editor.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, countKey]);

  // ── Catalogue picker ──────────────────────────────────────────────────────
  const [pickerOpen, setPickerOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [catalogue, setCatalogue] = useState({ status: 'idle', entries: [] });

  useEffect(() => {
    if (!pickerOpen || catalogue.status !== 'idle') return undefined;
    let cancelled = false;
    setCatalogue({ status: 'loading', entries: [] });
    loadCatalogue()
      .then((entries) => { if (!cancelled) setCatalogue({ status: 'ok', entries }); })
      .catch(() => { if (!cancelled) setCatalogue({ status: 'error', entries: [] }); });
    return () => { cancelled = true; };
  }, [pickerOpen, catalogue.status]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    return catalogue.entries
      .filter((e) => !pickedKeys.has(e.key))
      .filter((e) => !q || e.label.toLowerCase().includes(q) || e.kind.toLowerCase().includes(q))
      .sort((a, b) => {
        // Whatever the client is typing should be at the top; ties fall back to the option
        // that actually has hotels behind it.
        const aHit = q && a.label.toLowerCase().startsWith(q) ? 0 : 1;
        const bHit = q && b.label.toLowerCase().startsWith(q) ? 0 : 1;
        return aHit - bHit || (b.hotels ?? 0) - (a.hotels ?? 0) || a.label.localeCompare(b.label);
      })
      .slice(0, 24);
  }, [catalogue.entries, pickedKeys, query]);

  const chooseFromPicker = (entry) => {
    addCriterion(entry);
    setQuery('');
  };

  return (
    <div className={styles.vacSearch}>
      <span className={styles.liveLabel}>What this card searches for</span>

      <div className={styles.vacPresets}>
        {VACATION_FILTER_PRESETS.map((p) => {
          const on = pickedKeys.has(critKey(p));
          return (
            <button
              key={critKey(p)}
              type="button"
              className={`${styles.vacPreset} ${on ? styles.vacPresetOn : ''}`}
              aria-pressed={on}
              onClick={() => togglePreset(p)}
            >
              {on && <Check size={12} />}
              {p.label}
            </button>
          );
        })}
      </div>

      {picked.length === 0 ? (
        <p className={styles.destEmpty}>
          Nothing picked yet — the card is shown but is not clickable on the website.
        </p>
      ) : (
        <div className={styles.destChips}>
          {picked.map((c) => (
            <span key={critKey(c)} className={styles.destChip}>
              <span className={`${styles.destChipKind} ${styles.vacChipKind}`}>
                {c.filterKey === 'boards' ? 'Board' : c.filterKey === 'adultsOnly' ? 'Guests' : 'Filter'}
              </span>
              <span className={styles.destChipName}>{c.label}</span>
              <button
                type="button"
                className={`${styles.destChipBtn} ${styles.destChipRemove}`}
                title={`Remove ${c.label}`}
                onClick={() => removeCriterion(critKey(c))}
              >
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      )}

      {pickerOpen ? (
        <div className={styles.vacPicker}>
          <div className={styles.hotelPicker}>
            <div className={styles.hotelPickerInput}>
              <Search size={14} />
              <input
                autoFocus
                value={query}
                placeholder="Search facilities, activities, property types…"
                onChange={(e) => setQuery(e.target.value)}
              />
              <button
                type="button"
                className={styles.hotelPickerClear}
                title="Close"
                onClick={() => {
                  setPickerOpen(false);
                  setQuery('');
                  // A failed load sticks until the picker is reopened, so clear it here or
                  // the catalogue would never be retried.
                  setCatalogue((c) => (c.status === 'error' ? { status: 'idle', entries: [] } : c));
                }}
              >
                <X size={13} />
              </button>
            </div>
            {catalogue.status === 'loading' && (
              <span className={styles.fieldHint}>Loading the filter list…</span>
            )}
            {catalogue.status === 'error' && (
              <span className={styles.fieldHint}>
                Could not load the filter list. The buttons above still work.
              </span>
            )}
            {catalogue.status === 'ok' && (
              <span className={styles.fieldHint}>
                {matches.length === 0
                  ? (query.trim() ? `Nothing matches “${query.trim()}”.` : 'Everything is already picked.')
                  // The facets endpoint returns each group's most common entries, not the whole
                  // catalogue — which is the right cut here, since a rarer amenity could only
                  // ever produce a thin card.
                  : 'The filters hotels in these destinations actually offer.'}
              </span>
            )}
            {matches.length > 0 && (
              <div className={`${styles.hotelPickerDropdown} ${styles.vacPickerDropdown}`}>
                {matches.map((e) => (
                  <button
                    key={e.key}
                    type="button"
                    className={styles.hotelPickerOption}
                    onClick={() => chooseFromPicker(e)}
                  >
                    <span className={styles.hotelPickerText}>
                      <span className={styles.hotelPickerName}>{e.label}</span>
                      <span className={styles.hotelPickerMeta}>
                        {e.hotels != null
                          ? `${e.kind} · ${Number(e.hotels).toLocaleString()} hotels`
                          : e.kind}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : (
        <button type="button" className={styles.smallBtn} onClick={() => setPickerOpen(true)}>
          <Plus size={14} />
          Add another filter
        </button>
      )}

      <div className={styles.vacCount}>
        {count.status === 'idle' && !boardsOnly && (
          <span className={styles.vacCountIdle}>Pick a filter to see how many hotels match.</span>
        )}
        {boardsOnly && (
          <span className={styles.vacCountIdle}>
            Board type is matched against live prices, not the hotel record, so there is
            nothing to count here — add a star rating or a facility to check the card.
          </span>
        )}
        {count.status === 'loading' && <span className={styles.vacCountIdle}>Checking…</span>}
        {count.status === 'error' && (
          <span className={styles.vacCountIdle}>Could not check how many hotels match.</span>
        )}
        {count.status === 'ok' && count.hotels === 0 && (
          <span className={styles.vacCountBad}>
            <AlertTriangle size={13} />
            No hotels match — this card would open an empty results page.
          </span>
        )}
        {count.status === 'ok' && count.hotels > 0 && count.hotels < VACATION_LOW_MATCH && (
          <span className={styles.vacCountWarn}>
            <AlertTriangle size={13} />
            Only {count.hotels.toLocaleString()} hotels match — too thin for a homepage card.
          </span>
        )}
        {count.status === 'ok' && count.hotels >= VACATION_LOW_MATCH && (
          <span className={styles.vacCountOk}>
            <Check size={13} />
            ≈{count.hotels.toLocaleString()} hotels match
          </span>
        )}
        {count.status === 'ok' && picked.some((c) => c.filterKey === 'boards') && (
          <span className={styles.fieldHint}>
            Board type is not part of this count — it is applied when live prices load.
          </span>
        )}
        <span className={styles.fieldHint}>
          Counted across the {VACATION_COUNT_SCOPE.length} destinations the homepage searches
          by default. Saving never waits for this check.
        </span>
      </div>

      <label className={styles.vacFacts}>
        <input
          type="checkbox"
          checked={showFacts !== false}
          // The criteria ride along even though this toggle does not change them: on a legacy
          // board-only card they are still implicit in `boardCode`, and a patch without them
          // would let the caller's mirror rule blank the board and unlink the card.
          onChange={(e) => onPatch({ criteria: picked, showFacts: e.target.checked })}
        />
        Show these filters as small tags on the card
      </label>
    </div>
  );
}
